import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getByIdPA, patchByIdPA } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaCreditCard, 
  FaFileAlt, 
  FaDollarSign, 
  FaUsers, 
  FaCalendarAlt, 
  FaSave,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPen
} from 'react-icons/fa'
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap'
import './UpdatePlan.css'

// eslint-disable-next-line
export default function (props) {
  const navigate = useNavigate()
  const { authenticatedUser, setAlert } = useUserContext()
  const params = useParams()

  const [planName, setPlanName] = useState('')
  const [price, setPrice] = useState('')
  const [maxGuests, setMaxGuests] = useState('')
  const [months, setMonths] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const handleChangePlanName = (e) => setPlanName(e.target.value)
  const handleChangePrice = (e) => setPrice(e.target.value)
  const handleChangeMaxGuests = (e) => setMaxGuests(e.target.value)
  const handleChangeMonths = (e) => setMonths(e.target.value)
  const handleChangeDescription = (e) => setDescription(e.target.value)

  const validateForm = () => {
    const newErrors = {}

    if (!planName.trim()) {
      newErrors.planName = 'El nombre del plan es requerido'
    }

    if (!price || parseFloat(price) <= 0) {
      newErrors.price = 'El precio debe ser mayor a 0'
    }

    if (!maxGuests || parseInt(maxGuests) <= 0) {
      newErrors.maxGuests = 'El número máximo de invitados debe ser mayor a 0'
    }

    if (!months || parseInt(months) <= 0) {
      newErrors.months = 'El número de meses debe ser mayor a 0'
    }

    if (!description.trim()) {
      newErrors.description = 'La descripción es requerida'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    
    try {
      let record = await patchByIdPA({ 
        path: 'plans',
        id: params.id,
        data: {
          planName: planName.trim(),
          price: parseFloat(price),
          maxGuests: parseInt(maxGuests),
          months: parseInt(months),
          description: description.trim(),
        }, 
        token: authenticatedUser.value 
      })

      if(Object.prototype.hasOwnProperty.call(record, 'planId')) {
        setAlert({
          message: 'Plan actualizado exitosamente',
          msgType: 'success'
        })
        navigate('/planes')
      } else {
        setAlert({
          message: record.message || 'Error al actualizar el plan',
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al actualizar el plan',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const fetchData = async () => {
    try {
      const plan = await getByIdPA({ path: 'plans', id: params.id, token: authenticatedUser.value })
      
      if (plan && typeof plan === 'object') {
        setPlanName(plan.planName || '')
        setPrice(plan.price || '')
        setMaxGuests(plan.maxGuests || '')
        setMonths(plan.months || '')
        setDescription(plan.description || '')
      } else {
        setAlert({
          message: 'Error al cargar los datos del plan',
          msgType: 'warning'
        })
        navigate('/planes')
      }
    } catch (error) {
      setAlert({
        message: 'Error al cargar los datos del plan',
        msgType: 'warning'
      })
      navigate('/planes')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <div className="update-plan-container">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <FaPen />
            </div>
            <div className="header-text">
              <h1>Editar Plan</h1>
              <p>Modifica la información del plan de suscripción</p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <Card className="form-card">
          <Card.Header>
            <h3>
              <FaCreditCard className="field-icon" />
              Información del Plan
            </h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                {/* Plan Name Field */}
                <Col lg={12} md={12} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaFileAlt className="field-icon" />
                      Nombre del Plan
                      <span className="required-field">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Nombre del plan de suscripción"
                      value={planName}
                      onChange={handleChangePlanName}
                      className={`form-control-custom ${errors.planName ? 'is-invalid' : ''}`}
                    />
                    {errors.planName && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.planName}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* Price and Max Guests Fields */}
                <Col lg={6} md={6} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaDollarSign className="field-icon" />
                      Precio
                      <span className="required-field">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={price}
                      onChange={handleChangePrice}
                      className={`form-control-custom ${errors.price ? 'is-invalid' : ''}`}
                    />
                    {errors.price && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.price}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={6} md={6} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaUsers className="field-icon" />
                      Máximo de Invitados
                      <span className="required-field">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="Número de invitados"
                      value={maxGuests}
                      onChange={handleChangeMaxGuests}
                      className={`form-control-custom ${errors.maxGuests ? 'is-invalid' : ''}`}
                    />
                    {errors.maxGuests && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.maxGuests}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* Months and Description Fields */}
                <Col lg={6} md={6} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaCalendarAlt className="field-icon" />
                      Duración (Meses)
                      <span className="required-field">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      placeholder="Número de meses"
                      value={months}
                      onChange={handleChangeMonths}
                      className={`form-control-custom ${errors.months ? 'is-invalid' : ''}`}
                    />
                    {errors.months && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.months}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={6} md={6} sm={12} className="mb-4">
                  <Form.Group>
                    <Form.Label className="form-label">
                      <FaFileAlt className="field-icon" />
                      Descripción
                      <span className="required-field">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Descripción del plan"
                      value={description}
                      onChange={handleChangeDescription}
                      className={`form-control-custom ${errors.description ? 'is-invalid' : ''}`}
                    />
                    {errors.description && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.description}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Form Actions */}
              <div className="form-actions">
                <Button 
                  type="button" 
                  variant="outline-secondary"
                  className="cancel-button"
                  onClick={() => navigate('/planes')}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Actualizando Plan...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" />
                      Actualizar Plan
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}
